import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { LoadingCard } from '@/components/ui/loading'
import { Navigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/context/auth-context'
import SimpleIndividualDashboard from '@/components/dashboard/SimpleIndividualDashboard'
import ProviderDashboardContent from '@/components/dashboard/ProviderDashboardContent'
import { recordsAPI } from '@/lib/api'
import type { HealthRecord } from '@/lib/types'

interface DashboardStats {
  totalRecords: number
  personalLogs: number
  medicalRecords: number
  recentActivity: HealthRecord[]
}

interface ProviderDashboardStats {
  totalUsers: number
  activeTokens: number
  verificationStatus: string
  institutionScore: number
  recentActivity: any[]
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | ProviderDashboardStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const { user, isLoading: authLoading } = useAuth()

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return

      try {
        if (user.userType === 'individual') {
          // Fetch real dashboard stats from API
          const response = await recordsAPI.getDashboardStats()
          if (response.success) {
            setStats({
              totalRecords: response.data.totalRecords || 0,
              personalLogs: response.data.personalLogs || 0,
              medicalRecords: response.data.medicalRecords || 0,
              recentActivity: response.data.recentActivity || []
            })
          } else {
            throw new Error(response.message || 'Failed to fetch stats')
          }
        } else {
          // Provider stats - TODO: implement provider dashboard API
          setStats({
            totalUsers: 0,
            activeTokens: 0,
            verificationStatus: user.profile.isVerified ? 'verified' : 'pending',
            institutionScore: user.profile.isVerified ? 95 : 75,
            recentActivity: []
          })
        }
      } catch (error: any) {
        console.error('Failed to fetch dashboard stats:', error)
        // Set default stats on error so dashboard still renders
        if (user.userType === 'individual') {
          setStats({
            totalRecords: 0,
            personalLogs: 0,
            medicalRecords: 0,
            recentActivity: []
          })
        }
        toast({
          title: "Failed to load statistics",
          description: "Unable to fetch your dashboard statistics. Showing default view.",
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
  }, [user, toast])

  // Handle authentication loading
  if (authLoading) {
    return (
      <div className="space-y-8">
        <div className="space-y-2">
          <div className="h-8 w-48 skeleton" />
          <div className="h-4 w-96 skeleton" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <LoadingCard key={i} />
          ))}
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Handle dashboard data loading
  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Loading Header */}
        <div className="space-y-2">
          <div className="h-8 w-48 skeleton" />
          <div className="h-4 w-96 skeleton" />
        </div>

        {/* Loading Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <LoadingCard key={i} />
          ))}
        </div>

        {/* Loading Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LoadingCard />
          <LoadingCard />
        </div>
      </div>
    )
  }

  // Unified Dashboard - Dynamic content based on user type
  return (
    <div className="unified-dashboard space-y-6">
      {/* Account Type Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {user.userType === 'individual' ? 'Personal Health Dashboard' : (user.profile as any)?.institutionName || 'Provider Dashboard'}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={`text-sm font-medium ${user.userType === 'individual'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-purple-100 text-purple-800'
              }`}>
              {user.userType === 'individual' ? '👤 Individual Account' : '🏥 Provider Account'}
            </Badge>
            {user.profile?.isVerified && (
              <Badge className="bg-green-100 text-green-800 text-sm font-medium">
                ✓ Verified
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Dynamic Dashboard Content based on User Type */}
      {user.userType === 'individual' ? (
        <SimpleIndividualDashboard user={user} stats={stats as DashboardStats} />
      ) : (
        <ProviderDashboardContent user={user} stats={stats as ProviderDashboardStats} />
      )}
    </div>
  )
}